// Integración con el widget de chat BKChat (simply-chat-widget).
//
// El snippet de public/index.html solo carga el script y deja `window.BKChat`
// como cola de comandos. El `init` se hace desde aquí, una única vez, cuando
// la app ya sabe si hay usuario logueado (con `user`) o no (anónimo). Los
// cambios posteriores de sesión (login/logout sin recargar) van por
// `identify`.
//
// Motivo de no hacer `init` anónimo en el HTML: el widget no protege la
// carrera entre un `init` en vuelo y un `identify` inmediato; ambos flujos
// terminan y gana el último, así que un usuario logueado podía quedarse
// como anónimo. Por eso, además, `identify` espera a que el flujo anterior
// haya terminado (eventos `identified` / `identify:error`).

const APP_ID = 'SIMPLYTEAM_PROD';
// Si el widget nunca avisa (script bloqueado, red caída…), no bloqueamos
// indefinidamente la siguiente identificación.
const SETTLE_TIMEOUT_MS = 8000;

export interface ChatUser {
  id: string;
  name: string;
  email: string;
}

declare global {
  interface Window {
    BKChat?: (command: string, ...args: unknown[]) => void;
  }
}

let initialized = false;
let settled = true; // false mientras hay un init/identify en vuelo
let settleTimer: ReturnType<typeof setTimeout> | null = null;
let current: ChatUser | null = null; // última identidad enviada
let pending: ChatUser | null | undefined; // identidad a enviar cuando termine el flujo en vuelo

function bk(): Window['BKChat'] | null {
  return typeof window.BKChat === 'function' ? window.BKChat : null;
}

function sameUser(a: ChatUser | null, b: ChatUser | null): boolean {
  if (a == null || b == null) return a == null && b == null;
  return a.id === b.id && a.name === b.name && a.email === b.email;
}

function markInFlight(): void {
  settled = false;
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(onSettled, SETTLE_TIMEOUT_MS);
}

function onSettled(): void {
  if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
  settled = true;
  if (pending !== undefined) {
    const next = pending;
    pending = undefined;
    if (!sameUser(next, current)) send(next);
  }
}

function send(user: ChatUser | null): void {
  const w = bk();
  if (!w) return;
  current = user;
  markInFlight();
  if (user) w('identify', user);
  else w('identify');
}

// Fija la identidad del usuario en el widget. La primera llamada hace el
// `init` (anónimo si `user` es null); las siguientes usan `identify`.
export function setChatUser(user: ChatUser | null): void {
  const w = bk();
  if (!w) return;

  if (!initialized) {
    initialized = true;
    current = user;
    w('on', 'identified', onSettled);
    w('on', 'identify:error', onSettled);
    markInFlight();
    w('init', user ? { appId: APP_ID, user } : { appId: APP_ID });
    return;
  }

  if (!settled) {
    // Hay un flujo en vuelo: se enviará al terminar (solo el último valor).
    pending = user;
    return;
  }
  if (sameUser(user, current)) return;
  send(user);
}
